import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectAdminToken } from "../../admin/sessionSlice";
import { EXTRAMILE_GUEST_JOIN, EXTRAMILE_SIGNUP } from "../../config/extramileJoinUrls";
import "./host.css";

const CURRENT_LOBBY_KEY = "mythFactCurrentLobby";

function readStoredUserData() {
  try {
    const raw =
      localStorage.getItem("userData") ||
      sessionStorage.getItem("userData") ||
      "{}";
    return JSON.parse(raw) || {};
  } catch {
    return {};
  }
}

function resolveLobbyName(locationState) {
  const fromState =
    typeof locationState?.lobbyName === "string" ? locationState.lobbyName.trim() : "";
  if (fromState) return fromState;
  try {
    const ss = sessionStorage.getItem(CURRENT_LOBBY_KEY);
    if (ss && ss.trim()) return ss.trim();
  } catch {
    // ignore storage failures
  }
  return "main";
}

function randomPin4() {
  return Math.floor(1000 + Math.random() * 9000);
}

function buildExtramileJoinUrl(isGuest, gameId, sessionId) {
  const base = (isGuest ? EXTRAMILE_GUEST_JOIN : EXTRAMILE_SIGNUP).replace(/\/$/, "");
  const u = new URL(base);
  u.searchParams.set("gameId", String(gameId ?? ""));
  u.searchParams.set("sessionId", String(sessionId ?? ""));
  return u.toString();
}

/** Append lobby + share flags (same pattern as host/qr.jsx). */
function appendLobbyShareFlagsToJoinUrl(baseUrl, lobbyId) {
  const base = String(baseUrl ?? "").trim();
  const id = String(lobbyId ?? "").trim();
  if (!base) return "";
  if (!id) return base;
  try {
    const u = new URL(base);
    u.searchParams.set("lobby", id);
    u.searchParams.set("fromShare", "1");
    u.searchParams.set("shared", "1");
    return u.toString();
  } catch {
    return base;
  }
}

function deriveGuestFromUser(u) {
  const t = String(u?.user_type ?? u?.userType ?? "").toLowerCase();
  if (t === "guest") return true;
  const src = String(u?.source ?? u?.roles ?? "").toUpperCase();
  return src === "GUEST_USER" || src === "GUEST";
}

function buildFallbackAppLink(pin, lobbyName) {
  const base = String(process.env.REACT_APP_BASE_URL || window.location.origin || "").replace(/\/+$/, "");
  const p = String(pin || "").trim();
  const l = encodeURIComponent(String(lobbyName || "main"));
  return `${base}/game?pin=${p}&lobby=${l}`;
}

function Host() {
  const navigate = useNavigate();
  const location = useLocation();
  const adminToken = useSelector(selectAdminToken);
  const userData = useMemo(() => readStoredUserData(), []);
  const backendBase = useMemo(
    () => String(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, ""),
    []
  );

  const lobbyName = useMemo(() => resolveLobbyName(location.state), [location.state]);
  const [pin] = useState(() => {
    const statePin = Number(location.state?.pin);
    return Number.isFinite(statePin) && statePin > 0 ? Math.trunc(statePin) : randomPin4();
  });
  const isGuest = useMemo(() => deriveGuestFromUser(userData), [userData]);

  const joinUrl = useMemo(() => {
    const gameId = String(userData.gameId ?? "").trim();
    const sessionId = String(userData.sessionId ?? "").trim();
    const lobbyId = String(lobbyName ?? "").trim();
    if (gameId && sessionId) {
      const base = buildExtramileJoinUrl(isGuest, gameId, sessionId);
      return appendLobbyShareFlagsToJoinUrl(base, lobbyId);
    }
    const fromState =
      typeof location.state?.link === "string" ? location.state.link.trim() : "";
    if (fromState) return fromState;
    return buildFallbackAppLink(pin, lobbyId);
  }, [isGuest, lobbyName, location.state?.link, pin, userData.gameId, userData.sessionId]);
  const [copyLabel, setCopyLabel] = useState("Copy Link");
  const copyResetTimerRef = useRef(null);

  const resetCopyLabelLater = (delayMs = 10000) => {
    if (copyResetTimerRef.current) {
      window.clearTimeout(copyResetTimerRef.current);
    }
    copyResetTimerRef.current = window.setTimeout(() => {
      setCopyLabel("Copy Link");
      copyResetTimerRef.current = null;
    }, delayMs);
  };

  const handleCopyLink = async () => {
    const text = String(joinUrl || "").trim();
    if (!text) {
      setCopyLabel("Copy Link");
      return;
    }
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopyLabel("Copied");
      resetCopyLabelLater(10000);
    } catch {
      setCopyLabel("Copy failed");
      resetCopyLabelLater(3000);
    }
  };

  const handleDownloadQr = async () => {
    try {
      const resp = await fetch(qrSrc);
      if (!resp.ok) throw new Error("QR download failed");
      const blob = await resp.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = `what-would-you-do-qr-${String(pin).padStart(4, "0")}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
    } catch {
      window.open(qrSrc, "_blank", "noopener,noreferrer");
    }
  };

  useEffect(() => {
    document.documentElement.classList.add("host-static-bg");
    document.body.classList.add("host-static-bg");
    return () => {
      document.documentElement.classList.remove("host-static-bg");
      document.body.classList.remove("host-static-bg");
    };
  }, []);

  const qrSrc = useMemo(() => {
    return `https://api.qrserver.com/v1/create-qr-code/?size=220x220&margin=2&data=${encodeURIComponent(joinUrl)}`;
  }, [joinUrl]);

  useEffect(() => {
    const saveStageAccess = async () => {
      const userId = userData.userId || userData.userid || userData.id;
      const sessionId = userData.sessionId;
      const organizationId = userData.organizationId;
      if (!backendBase || !adminToken || !userId || !sessionId || !organizationId) return;

      try {
        const resp = await fetch(`${backendBase}/saveHostJoinAccess`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            userId,
            sessionId,
            organizationId,
            lobby: lobbyName,
            login_pin: pin,
            link: joinUrl,
          }),
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok || !data?.success) {
          throw new Error(data?.message || "Could not save link/pin");
        }
      } catch (err) {
        console.error("host saveHostJoinAccess:", err);
      }
    };

    saveStageAccess();
  }, [adminToken, backendBase, joinUrl, lobbyName, pin, userData]);

  useEffect(() => {
    return () => {
      if (copyResetTimerRef.current) {
        window.clearTimeout(copyResetTimerRef.current);
      }
    };
  }, []);

  const handleStart = () => {
    navigate("/game", {
      replace: false,
      state: {
        pin,
        link: joinUrl,
        lobbyName,
      },
    });
  };

  return (
    <div className="host-landing">
      <div className="host-cards">
        <div className="host-column">
          <section className="host-card host-card--create" aria-label="Create">
            <div className="host-card-title">CREATE</div>
            <div className="host-card-inner">
              <div className="host-qr-frame" aria-label="QR code">
                <img src={qrSrc} alt="Join QR code" className="host-qr-image" />
              </div>
              <p className="host-card-subtitle">scan to join</p>
            </div>
          </section>
          <button type="button" className="host-card-btn host-card-btn--create" onClick={handleDownloadQr}>
            Download Qr
          </button>
        </div>

        <div className="host-column">
          <section className="host-card host-card--join" aria-label="Join">
            <div className="host-card-title">JOIN</div>
            <div className="host-card-inner">
              <div className="host-pin-frame" aria-label="PIN">
                <div className="host-pin">{String(pin).padStart(4, "0")}</div>
              </div>
            </div>
          </section>
          <button type="button" className="host-card-btn host-card-btn--join" onClick={handleCopyLink}>
            {copyLabel}
          </button>
        </div>
      </div>

      <button type="button" className="host-start-btn" onClick={handleStart}>
        Start
      </button>
    </div>
  );
}

export default Host;
